import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, DollarSign, Trash2, Search, Calendar as CalendarIcon, Filter, X, Edit } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateLocal } from '@/utils/helpers';

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  
  const [editingExpense, setEditingExpense] = useState(null);
  
  // Form State
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    category_id: '',
    date: new Date().toISOString().split('T')[0],
  });

  // Filter State
  const [filters, setFilters] = useState({
    description: '',
    category_id: 'all',
    user_id: 'all',
    start_date: '',
    end_date: '',
    month: new Date().toISOString().slice(0, 7), // Default to current month YYYY-MM
    use_custom_dates: false
  });

  useEffect(() => {
    fetchExpenses();
    fetchCategories();
    fetchUsers();
  }, [filters.month, filters.use_custom_dates]); // Refetch when main view mode changes

  const fetchExpenses = async () => {
    try {
      const params = {};
      
      if (filters.use_custom_dates) {
        if (filters.start_date) params.start_date = filters.start_date;
        if (filters.end_date) params.end_date = filters.end_date;
      } else {
        params.month = filters.month;
      }
      
      if (filters.description) params.description = filters.description;
      if (filters.category_id && filters.category_id !== 'all') params.category_id = filters.category_id;
      if (filters.user_id && filters.user_id !== 'all') params.user_id = filters.user_id;

      const response = await axios.get('/expenses', { params });
      setExpenses(response.data);
    } catch (error) {
      toast.error('Error al cargar gastos');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/expense-categories');
      setCategories(response.data);
    } catch (error) {
      toast.error('Error al cargar categorías');
    }
  };

  const fetchUsers = async () => {
    if (!isAdmin()) return;
    try {
      const response = await axios.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users', error);
    }
  };

  const isAdmin = () => {
    const userStr = localStorage.getItem('user');
    if (!userStr) return false;
    try {
      const user = JSON.parse(userStr);
      return user.role === 'admin';
    } catch {
      return false;
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchExpenses();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
      };

      if (editingExpense) {
        await axios.put(`/expenses/${editingExpense.id}`, data);
        toast.success('Gasto actualizado');
      } else {
        await axios.post('/expenses', data);
        toast.success('Gasto registrado');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar gasto');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      description: expense.description,
      amount: expense.amount.toString(),
      category_id: expense.category_id || '',
      date: expense.date || new Date().toISOString().split('T')[0],
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('¿Estás seguro de eliminar este gasto?')) return;

    try {
      await axios.delete(`/expenses/${expenseId}`);
      toast.success('Gasto eliminado');
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar gasto');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    try {
      if (editingCategory) {
        await axios.put(`/expense-categories/${editingCategory.id}`, { name: newCategory });
        toast.success('Categoría actualizada');
        setEditingCategory(null);
      } else {
        await axios.post('/expense-categories', { name: newCategory });
        toast.success('Categoría agregada');
      }
      setNewCategory('');
      fetchCategories();
      // Also refresh expenses to show updated category names
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar categoría');
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setNewCategory(category.name);
  };

  const handleCancelEditCategory = () => {
    setEditingCategory(null);
    setNewCategory('');
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!categoryId) return;
    if (!window.confirm('¿Eliminar esta categoría?')) return;
    
    try {
      await axios.delete(`/expense-categories/${categoryId}`);
      toast.success('Categoría eliminada');
      fetchCategories();
      // Also refresh expenses to show updated category names
      fetchExpenses();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar categoría');
    }
  };

  const resetForm = () => {
    setEditingExpense(null);
    setFormData({ 
      description: '', 
      amount: '', 
      category_id: '', 
      date: new Date().toISOString().split('T')[0] 
    });
  };

  const getTotalExpenses = () => {
    return expenses.reduce((sum, exp) => sum + exp.amount, 0);
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.name : 'Sin categoría';
  };

  return (
    <div className="expenses-page" data-testid="expenses-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Gastos</h1>
          <p className="page-subtitle">Registra y controla los gastos del negocio</p>
        </div>
        <div className="header-actions">
          <Button 
            variant="outline" 
            onClick={() => setIsCategoryDialogOpen(true)}
            data-testid="manage-categories-btn"
          >
            Categorías
          </Button>
          <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} data-testid="add-expense-btn">
            <Plus size={20} />
            Registrar Gasto
          </Button>
        </div>
      </div>

      <Card className="total-card">
        <div className="total-content">
          <div className="total-icon">
            <DollarSign size={32} />
          </div>
          <div>
            <p className="total-label">Total de Gastos</p>
            <h2 className="total-value" data-testid="total-expenses">${getTotalExpenses().toFixed(2)}</h2>
          </div>
        </div>
      </Card>

      {/* Filters Section */}
      <Card className="filters-card">
        <form onSubmit={handleSearch} className="filters-form">
          <div className="filters-grid">
            <div className="filter-group">
              <Label>Descripción</Label>
              <div className="search-input-wrapper">
                <Search className="search-icon" size={16} />
                <Input 
                  placeholder="Buscar..." 
                  value={filters.description}
                  onChange={(e) => setFilters({...filters, description: e.target.value})}
                />
              </div>
            </div>

            <div className="filter-group">
              <Label>Categoría</Label>
              <Select 
                value={filters.category_id} 
                onValueChange={(val) => setFilters({...filters, category_id: val})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="uncategorized">Sin categoría</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isAdmin() && (
              <div className="filter-group">
                <Label>Usuario</Label>
                <Select 
                  value={filters.user_id} 
                  onValueChange={(val) => setFilters({...filters, user_id: val})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.username}>{user.username}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="filter-group date-toggle-group">
              <div className="flex items-center justify-between">
                <Label>Periodo</Label>
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm" 
                  className="text-xs h-1 p-0"
                  onClick={() => setFilters({
                    ...filters, 
                    use_custom_dates: !filters.use_custom_dates,
                    start_date: '',
                    end_date: ''
                  })}
                >
                  {filters.use_custom_dates ? 'Ver por Mes' : 'Rango Personalizado'}
                </Button>
              </div>
              
              {filters.use_custom_dates ? (
                <div className="flex gap-2">
                  <Input 
                    type="date" 
                    value={filters.start_date}
                    onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                  />
                  <Input 
                    type="date" 
                    value={filters.end_date}
                    onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                  />
                </div>
              ) : (
                <Input 
                  type="month" 
                  value={filters.month}
                  onChange={(e) => setFilters({...filters, month: e.target.value})}
                />
              )}
            </div>
          </div>
          
          <div className="filters-actions">
            <Button type="submit" variant="secondary">
              <Filter size={16} className="mr-2" />
              Filtrar
            </Button>
            <Button 
              type="button" 
              variant="ghost" 
              onClick={() => {
                setFilters({
                  description: '',
                  category_id: 'all',
                  user_id: 'all',
                  start_date: '',
                  end_date: '',
                  month: new Date().toISOString().slice(0, 7),
                  use_custom_dates: false
                });
                fetchExpenses();
              }}
            >
              Limpiar
            </Button>
          </div>
        </form>
      </Card>

      <div className="expenses-list">
        {expenses.map((expense) => (
          <Card key={expense.id} className="expense-card" data-testid={`expense-card-${expense.id}`}>
            <div className="expense-main">
              <div className="expense-info">
                <h3 className="expense-description">{expense.description}</h3>
                <div className="expense-meta">
                  <span className="expense-category">
                    {expense.category_name || getCategoryName(expense.category_id)}
                  </span>
                  <span className="expense-date">
                    <CalendarIcon size={14} className="inline mr-1" />
                    {formatDateLocal(expense.date)}
                  </span>
                  <span className="expense-user">Por: {expense.created_by}</span>
                </div>
              </div>
              <div className="expense-actions">
                <div className="expense-amount">${expense.amount.toFixed(2)}</div>
                {isAdmin() && (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => handleEdit(expense)}
                    data-testid={`edit-expense-${expense.id}`}
                    className="mr-2"
                  >
                    <Edit size={16} />
                  </Button>
                )}
                <Button 
                  size="sm" 
                  variant="destructive" 
                  onClick={() => handleDelete(expense.id)}
                  data-testid={`delete-expense-${expense.id}`}
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {expenses.length === 0 && (
        <div className="empty-state">
          <DollarSign size={64} />
          <h3>No hay gastos registrados</h3>
          <p>Intenta cambiar los filtros o registra un nuevo gasto</p>
        </div>
      )}

      {/* Add Expense Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="expense-dialog">
          <DialogHeader>
            <DialogTitle>{editingExpense ? 'Editar Gasto' : 'Registrar Gasto'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="expense-form">
            {/* 1. Category */}
            <div className="form-group">
              <Label htmlFor="category">Categoría</Label>
              <Select 
                value={formData.category_id} 
                onValueChange={(val) => setFormData({...formData, category_id: val})}
              >
                <SelectTrigger data-testid="category-select">
                  <SelectValue placeholder="Selecciona una categoría" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* 2. Date */}
            <div className="form-group">
              <Label htmlFor="date">Fecha*</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                required
                data-testid="date-input"
              />
            </div>

            {/* 3. Amount */}
            <div className="form-group">
              <Label htmlFor="amount">Monto*</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                required
                data-testid="amount-input"
              />
            </div>

            {/* 4. Description */}
            <div className="form-group">
              <Label htmlFor="description">Descripción*</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Ej: Compra de equipamiento"
                required
                data-testid="description-input"
              />
            </div>

            <div className="form-actions">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} data-testid="save-expense-btn">
                {loading ? 'Guardando...' : (editingExpense ? 'Actualizar' : 'Guardar')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage Categories Dialog */}
      <Dialog open={isCategoryDialogOpen} onOpenChange={(open) => {
        setIsCategoryDialogOpen(open);
        if (!open) handleCancelEditCategory();
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Editar Categoría' : 'Gestionar Categorías'}</DialogTitle>
          </DialogHeader>
          <div className="category-form">
            <Label>{editingCategory ? 'Nombre de la Categoría' : 'Nueva Categoría'}</Label>
            <div className="flex gap-2">
              <Input
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="Ej: Servicios"
              />
              <Button onClick={handleAddCategory}>
                {editingCategory ? 'Actualizar' : 'Agregar'}
              </Button>
              {editingCategory && (
                <Button variant="outline" onClick={handleCancelEditCategory}>
                  Cancelar
                </Button>
              )}
            </div>
            
            <div className="category-list mt-4">
              <h4 className="font-semibold mb-2 text-sm text-gray-500">Categorías Existentes</h4>
              <div className="flex flex-col gap-2 max-h-[300px] overflow-y-auto">
                {categories.map(cat => (
                  <div key={cat.id} className="flex justify-between items-center p-2 bg-gray-50 rounded border">
                    <span>{cat.name}</span>
                    <div className="flex gap-1">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-8 w-8 p-0"
                        onClick={() => handleEditCategory(cat)}
                        title="Editar"
                      >
                        <Edit size={14} />
                      </Button>
                      <Button 
                        type="button"
                        size="sm" 
                        variant="ghost" 
                        className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCategory(cat.id);
                        }}
                        title="Eliminar"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end mt-4">
              <Button variant="outline" onClick={() => setIsCategoryDialogOpen(false)}>
                Cerrar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <style jsx>{`
        .expenses-page {
          max-width: 1400px;
        }

        .expense-dialog {
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .header-actions {
          display: flex;
          gap: 1rem;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .page-subtitle {
          color: #64748b;
        }

        .total-card {
          padding: 2rem;
          margin-bottom: 2rem;
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          border: none;
          box-shadow: 0 10px 25px rgba(239, 68, 68, 0.3);
        }

        .total-content {
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .total-icon {
          width: 72px;
          height: 72px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .total-label {
          font-size: 1rem;
          color: rgba(255, 255, 255, 0.9);
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .total-value {
          font-size: 2.5rem;
          font-weight: 800;
          color: white;
        }

        .filters-card {
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .filters-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .filters-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .search-input-wrapper {
          position: relative;
        }

        .search-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
        }

        .search-input-wrapper input {
          padding-left: 2.5rem;
        }

        .filters-actions {
          display: flex;
          justify-content: flex-end;
          gap: 0.5rem;
          margin-top: 0.5rem;
        }

        .expenses-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .expense-card {
          padding: 1.5rem;
          border: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          transition: all 0.3s;
        }

        .expense-card:hover {
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
          transform: translateY(-2px);
        }

        .expense-main {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
        }

        .expense-info {
          flex: 1;
          min-width: 0;
        }

        .expense-description {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .expense-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
          font-size: 0.875rem;
          color: #64748b;
        }

        .expense-category {
          padding: 0.25rem 0.75rem;
          background: #dbeafe;
          color: #1e40af;
          border-radius: 6px;
          font-weight: 600;
        }

        .expense-actions {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex-shrink: 0;
        }

        .expense-amount {
          font-size: 1.5rem;
          font-weight: 800;
          color: #ef4444;
          white-space: nowrap;
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          color: #94a3b8;
        }

        .empty-state h3 {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 1rem 0 0.5rem;
          color: #64748b;
        }

        .expense-form {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
          margin-top: 1rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-actions {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
          margin-top: 1rem;
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
          }

          .expense-main {
            flex-direction: column;
            align-items: flex-start;
          }

          .expense-actions {
            width: 100%;
            justify-content: space-between;
          }
          
          .filters-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default Expenses;
